import type { TableProps } from 'antd';
import { Gauge } from 'lucide-react';

import { DataTable } from '../../../components/shared';
import { Empty } from '../../../components/ui';
import { useT } from '../../../i18n';
import { cn } from '../../../lib/cn';
import { soVN } from '../../../lib/dinh-dang';
import { LOP_THANH, NHAN_GIOI_HAN } from '../constants';
import type { MucDung } from '../types';

/**
 * Mức dùng từng giới hạn trong license (ADR-0058 mục 8 "Phía khách"):
 * `214/300 người dùng`, kèm thanh tỉ lệ. Giới hạn runtime chưa đo vẫn hiện
 * mức tối đa, để quản trị viên biết license cho bao nhiêu.
 */
export function BangMucDung({ mucDung, dangTai }: { mucDung: MucDung[]; dangTai: boolean }) {
  const { t, locale } = useT();

  const nhan = (khoa: string) => (NHAN_GIOI_HAN[khoa] ? t(NHAN_GIOI_HAN[khoa]) : khoa);

  const cot: TableProps<MucDung>['columns'] = [
    {
      title: t('licenseCucBo.cotGioiHan'),
      dataIndex: 'khoa',
      render: (_v, m) => (
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="text-sm font-medium text-content-primary">{nhan(m.khoa)}</span>
          <span className="font-mono text-2xs text-content-muted">{m.khoa}</span>
        </div>
      ),
    },
    {
      title: t('licenseCucBo.cotMucDung'),
      width: 220,
      render: (_v, m) =>
        m.dangDung === null ? (
          <span className="text-xs text-content-muted">
            {t('licenseCucBo.chuaDo', { toiDa: soVN(m.toiDa, locale) })}
          </span>
        ) : (
          <span className="whitespace-nowrap text-sm tabular-nums text-content-primary">
            {t(m.khoa === 'max_users' ? 'licenseCucBo.mucDungNguoiDung' : 'licenseCucBo.mucDungChung', {
              dung: soVN(m.dangDung, locale),
              toiDa: soVN(m.toiDa, locale),
            })}
          </span>
        ),
    },
    {
      title: t('licenseCucBo.cotTiLe'),
      width: 200,
      render: (_v, m) =>
        m.phanTram === null ? null : (
          <div className="flex items-center gap-2">
            <div
              className="h-1.5 flex-1 overflow-hidden rounded-full bg-subtle"
              role="meter"
              aria-label={nhan(m.khoa)}
              aria-valuemin={0}
              aria-valuemax={m.toiDa}
              aria-valuenow={m.dangDung ?? 0}
            >
              <div
                className={cn('h-full rounded-full', LOP_THANH[m.muc])}
                style={{ width: `${Math.min(100, m.phanTram)}%` }}
              />
            </div>
            <span className="w-10 text-right text-xs tabular-nums text-content-secondary">
              {soVN(m.phanTram, locale)}%
            </span>
          </div>
        ),
    },
  ];

  return (
    <DataTable<MucDung>
      caption={t('licenseCucBo.mucDungCaption')}
      rowKey="khoa"
      dataSource={mucDung}
      columns={cot}
      loading={dangTai}
      size="middle"
      pagination={false}
      locale={{
        emptyText: dangTai ? (
          ' '
        ) : (
          <Empty icon={Gauge} title={t('licenseCucBo.mucDungRong')} description={t('licenseCucBo.mucDungRongMo')} />
        ),
      }}
    />
  );
}
